import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { UserPlus, X, Search, Users, Mail, Clock, Building2, AlertTriangle } from 'lucide-react';
import { ticketsApi } from '../../api/tickets';
import Modal from '../Modal';
import type { TicketCollaborator, TicketCollaboratorInvite } from '../../types';

/* Who is attached to a ticket besides the assignees.
   A collaborator belongs to their OWN company — the CRM organization the
   ticket is for (AccessOne, Smart of SJ) — and is never made a member of
   your tenant. You can only add that organization's own people, so the
   picker is its contact list rather than a free-text address box. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  ticketId: string;
  collaborators: TicketCollaborator[];
  invites?: TicketCollaboratorInvite[];
  canManage: boolean;
  /** The ticket's organization, for the empty-state message. */
  organizationName?: string | null;
  onChanged?: () => void;
}

const initials = (name?: string | null) =>
  (name ?? '?').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?';

function AddCollaboratorModal({ ticketId, onClose, onAdded }: {
  ticketId: string; onClose: () => void; onAdded: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);

  // Only the ticket's organization can supply collaborators, so the server
  // decides the candidate list — there is no company-wide member search here.
  const { data, isLoading } = useQuery({
    queryKey: ['collaborator-candidates', ticketId],
    queryFn: () => ticketsApi.getCollaboratorCandidates(ticketId),
  });
  const organization = data?.organization ?? null;
  const contacts = useMemo(() => data?.contacts ?? [], [data]);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts
      .filter(c => !c.alreadyOn && !c.invited)
      .filter(c => !q || c.name.toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q));
  }, [contacts, search]);

  const mutation = useMutation({
    mutationFn: () => ticketsApi.addCollaborators(ticketId, {
      ...(selected.length > 0 ? { contactIds: selected } : {}),
      ...(emails.length > 0 ? { emails } : {}),
    }),
    onSuccess: ({ added, invited }) => {
      toast.success(
        added > 0 && invited > 0 ? `${added} added, ${invited} invited`
        : invited > 0 ? `${invited} invited — they get the ticket when they sign in`
        : added > 0 ? `${added} added`
        : 'Already on this ticket',
      );
      onAdded();
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not add collaborators'),
  });

  const toggle = (id: string) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const addEmail = (): boolean => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return true;
    if (!EMAIL_RE.test(e)) { toast.error('Enter a valid email address'); return false; }
    // Check against the organization here so the mistake is caught before
    // the round trip, with the same explanation the server would give.
    const known = contacts.find(c => c.email === e);
    if (!known) {
      toast.error(`${e} is not a contact of ${organization?.name ?? 'this organization'} — add them there first`);
      return false;
    }
    if (known.alreadyOn || known.invited) { toast.error(`${known.name} is already on this ticket`); return false; }
    if (!emails.includes(e)) setEmails(prev => [...prev, e]);
    setEmailInput('');
    return true;
  };

  const pending = emailInput.trim().toLowerCase();
  const pendingCounts = pending && EMAIL_RE.test(pending) && contacts.some(c => c.email === pending) ? 1 : 0;
  const total = selected.length + emails.length + pendingCounts;

  const submit = () => {
    if (pending && !addEmail()) return;
    const list = pending && pendingCounts && !emails.includes(pending) ? [...emails, pending] : emails;
    if (selected.length === 0 && list.length === 0) {
      toast.error('Pick someone from the organization');
      return;
    }
    setEmails(list);
    mutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      busy={mutation.isPending}
      title={organization ? `Add collaborators from ${organization.name}` : 'Add collaborators'}
      width={560}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={total === 0 || mutation.isPending} onClick={submit}>
            {mutation.isPending ? 'Adding…' : `Add${total > 0 ? ` (${total})` : ''}`}
          </button>
        </>
      }
    >
      {isLoading ? (
        <p className="text-sm text-muted py-4">Loading…</p>
      ) : !organization ? (
        <div className="space-y-3 py-2">
          <p className="text-sm text-fg inline-flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--scpa-status-warning)' }} />
            This ticket is not linked to an organization yet.
          </p>
          <p className="text-sm text-muted leading-relaxed">
            Collaborators come from the organization the ticket is for. Set one on the ticket
            (Edit → Organization), then its contacts will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Plain English, and specific about what "signed in before" means:
              people read "account" as their ctOneAuth login, but the marker
              is about whether they have used ctTicketing itself. */}
          <p className="text-xs text-muted leading-relaxed">
            Pick who at <b className="text-fg">{organization.name}</b> should see this ticket. They get this
            ticket only — they are <b className="text-fg">not added to your company</b>.
            Anyone new signs in with their normal ctOneAuth login and lands straight on the ticket.
          </p>

          {contacts.length === 0 ? (
            <p className="text-sm text-muted">
              {organization.name} has no contacts yet. Add them in the CRM, then come back here.
            </p>
          ) : (
            <>
              <div>
                <label className="label" htmlFor="collab-search">People at {organization.name}</label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    id="collab-search"
                    autoFocus
                    className="input text-sm pl-8"
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className="max-h-56 overflow-y-auto space-y-0.5 mt-2">
                  {available.length === 0 ? (
                    <p className="text-xs text-muted py-2">
                      {search.trim() ? 'Nobody there matches that.' : 'Everyone at this organization is already on the ticket.'}
                    </p>
                  ) : available.map(c => (
                    <label
                      key={c.contactId}
                      className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-row-odd ${c.email ? 'cursor-pointer' : 'opacity-60'}`}
                      title={c.email ? undefined : 'No email address on file — add one in the CRM to invite them'}
                    >
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 accent-[var(--scpa-primary)] shrink-0"
                        checked={selected.includes(c.contactId)}
                        disabled={!c.email && !c.userId}
                        onChange={() => toggle(c.contactId)}
                      />
                      <span className="w-7 h-7 rounded-full brand-gradient text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
                        {initials(c.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-fg truncate">{c.name}</span>
                        <span className="block text-[11px] text-muted truncate">{c.email ?? 'no email on file'}</span>
                      </span>
                      <span
                        className="text-[11px] text-muted shrink-0"
                        title={c.userId
                          ? 'Has used ctTicketing before — added to the ticket now'
                          : 'Has not used ctTicketing yet. They sign in with their usual ctOneAuth login and the ticket is waiting for them.'}
                      >
                        {c.userId ? 'added now' : 'on first sign-in'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Typing an address is a shortcut for the same list. */}
              <div>
                <label className="label" htmlFor="collab-email">Or type their email</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="w-3.5 h-3.5 text-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="collab-email"
                      type="email"
                      className="input text-sm pl-8"
                      placeholder={`name@${(organization.name || 'agency').toLowerCase().replace(/[^a-z0-9]/g, '')}.com`}
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary shrink-0"
                    onClick={addEmail}
                    disabled={!emailInput.trim()}
                    aria-label="Add this email address"
                  >
                    Add
                  </button>
                </div>
                <p className="text-[11px] text-muted mt-1">
                  The address has to belong to someone at {organization.name}.
                </p>
                {emails.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {emails.map(e => (
                      <span key={e} className="badge flex items-center gap-1">
                        {e}
                        <button
                          type="button"
                          onClick={() => setEmails(prev => prev.filter(x => x !== e))}
                          aria-label={`Remove ${e}`}
                          className="text-muted hover:text-fg"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function CollaboratorsPanel({ ticketId, collaborators, invites = [], canManage, organizationName, onChanged }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-activity', ticketId] });
    qc.invalidateQueries({ queryKey: ['collaborator-candidates', ticketId] });
    onChanged?.();
  };
  const onError = (e: any) => toast.error(e?.response?.data?.message ?? e.message ?? 'Could not remove');

  const removeMutation = useMutation({
    mutationFn: (userId: string) => ticketsApi.removeCollaborator(ticketId, userId),
    onSuccess: () => { toast.success('Collaborator removed'); invalidate(); },
    onError,
  });
  const withdrawMutation = useMutation({
    mutationFn: (inviteId: string) => ticketsApi.removeCollaboratorInvite(ticketId, inviteId),
    onSuccess: () => { toast.success('Invite withdrawn'); invalidate(); },
    onError,
  });

  const empty = collaborators.length === 0 && invites.length === 0;

  return (
    <div>
      {organizationName && (
        <p className="text-[11px] text-muted mb-2 inline-flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> From <span className="text-fg font-medium">{organizationName}</span>
        </p>
      )}

      {empty ? (
        <p className="text-xs text-muted">
          {canManage
            ? organizationName
              ? `Nobody yet. Add people from ${organizationName} to let them see this ticket.`
              : 'Nobody yet. Set an organization on this ticket to add its people.'
            : 'No collaborators.'}
        </p>
      ) : (
        <ul className="space-y-1">
          {collaborators.map(c => (
            <li key={c.id} className="flex items-center gap-2 text-xs bg-row-odd rounded-md px-2 py-1.5">
              <span className="w-6 h-6 rounded-full brand-gradient text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
                {initials(c.user?.name || c.user?.email)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-fg font-medium truncate">{c.user?.name || c.user?.email}</span>
                <span className="block text-[10px] text-muted">
                  {c.role ? (c.role === 'customer' ? 'uses the portal' : c.role) : organizationName || 'outside collaborator'}
                </span>
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={() => removeMutation.mutate(c.userId)}
                  disabled={removeMutation.isPending}
                  className="text-muted hover:text-err shrink-0"
                  title={`Remove ${c.user?.name ?? 'collaborator'}`}
                  aria-label={`Remove ${c.user?.name ?? 'collaborator'}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}

          {/* Invited, but they have not signed in yet. */}
          {invites.map(i => (
            <li key={i.id} className="flex items-center gap-2 text-xs bg-row-odd rounded-md px-2 py-1.5">
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'var(--scpa-primary-muted)', color: 'var(--scpa-status-warning)' }}
              >
                <Clock className="w-3 h-3" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-fg font-medium truncate">{i.email}</span>
                <span className="block text-[10px]" style={{ color: 'var(--scpa-status-warning)' }}>
                  waiting for them to sign in
                </span>
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={() => withdrawMutation.mutate(i.id)}
                  disabled={withdrawMutation.isPending}
                  className="text-muted hover:text-err shrink-0"
                  title={`Withdraw the invite to ${i.email}`}
                  aria-label={`Withdraw the invite to ${i.email}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <button type="button" className="btn btn-ghost text-xs py-1.5 mt-2" onClick={() => setAdding(true)}>
          <UserPlus className="w-3.5 h-3.5" /> Add collaborator
        </button>
      )}
      {!canManage && !empty && (
        <p className="text-[11px] text-muted mt-2 inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> Can see this ticket and work its tasks.
        </p>
      )}

      {adding && (
        <AddCollaboratorModal ticketId={ticketId} onClose={() => setAdding(false)} onAdded={invalidate} />
      )}
    </div>
  );
}
